import {
  IntegrationsService,
  isDarkMode,
  LegalService,
  OrganizationService,
  SubscriptionService,
  useApiEffect,
  useApiGetter,
  useRoutes,
} from '@localstack/services';
import { Box, Button, Link, styled, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DocumentType, LegalAcceptanceSource, Permission, PlanFamily } from '@localstack/types';

import { ArrowRightAlt as ArrowRightAltIcon } from '@mui/icons-material';

import { ExternalLink, SUB_ACTIVE_STATUSES } from '@localstack/constants';
import { StudentPlanLegalFineprint } from '@localstack/ui';

import { AppRoute, BASE_URL, GITHUB_STUDENT_APP_CLIENT_ID } from '~/config';

import { CustomerLayout } from '~/layouts';

import { useAuthProvider } from '~/hooks/useAuthProvider';

import { STUDENT_PLAN_DESCRIPTION } from '../constants';

import { FeedbackMessageBox } from './Github/components/FeedbackMessage';

const CenterBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  flexDirection: 'column',
  textAlign: 'center',
}));

const UnderText = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: isDarkMode(theme) ? grey[500] : grey[600],
}));

const UnderLink = styled(Link)(({ theme }) => ({
  color: isDarkMode(theme) ? grey[300] : grey[800],
}));

const GITHUB_AUTH_BASE_URL = 'https://github.com/login/oauth/authorize';

const LoadingState = () => (
  <Box px={4}>
    <Button variant="contained" color="primary" fullWidth sx={{ textTransform: 'none' }} disableElevation disabled>
      Checking eligibility...
    </Button>
  </Box>
);

const EnrollInProgram = (): ReactElement => {
  const redirect_uri = BASE_URL + AppRoute.STUDENT_VERIFICATION;
  const auth_link = `${GITHUB_AUTH_BASE_URL}?client_id=${GITHUB_STUDENT_APP_CLIENT_ID}&redirect_uri=${redirect_uri}`;
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box px={4}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ textTransform: 'none' }}
          disableElevation
          onClick={() => (window.location.href = auth_link)}
        >
          Verify with GitHub
        </Button>
      </Box>

      <UnderText>
        Our student benefits are available through the <br />
        <UnderLink href={ExternalLink.LOCALSTACK_FOR_STUDENTS} target="_blank" rel="noopener noreferrer">
          GitHub Student Developer Pack
        </UnderLink>
      </UnderText>
    </Box>
  );
};

const ErrorMessage = ({ message }: { message: string }): ReactElement => (
  <Box display="flex" flexDirection="column" gap={2}>
    <FeedbackMessageBox message={message} severity="failure" />
    <Button
      variant="contained"
      color="secondary"
      fullWidth
      disableElevation
      onClick={() => window.open(ExternalLink.GITHUB_STUDENT_DEVELOPER_PACK, '_blank')}
      endIcon={<ArrowRightAltIcon />}
    >
      GitHub Student Pack home
    </Button>
  </Box>
);

const EnrolledInProgram = (): ReactElement => {
  const { goto } = useRoutes();

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <FeedbackMessageBox message="You are now enrolled to the LocalStack Student plan." severity="success" />
      <Button
        variant="contained"
        color="primary"
        fullWidth
        disableElevation
        onClick={() => goto(AppRoute.GETTING_STARTED)}
      >
        Start LocalStack
      </Button>
    </Box>
  );
};

export const StudentVerification = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const { can } = useAuthProvider();
  const [verificationFailed, setVerificationFailed] = useState<boolean>(false);

  const code = searchParams.get('code');

  const { confirmStudentPlan, isLoading: isCheckingStudentStatus } = useApiEffect(IntegrationsService, [
    'confirmStudentPlan',
  ]);

  const { recordLegalAcceptance } = useApiEffect(LegalService, ['recordLegalAcceptance']);

  const { data: orgStats, isLoading: isOrgStatsLoading } = useApiGetter(
    OrganizationService,
    'getOrganizationStats',
    [],
  );
  const isSingleWorkspace = orgStats?.num_all_members === 1;

  const {
    data: subscriptions,
    mutate,
    isLoading: isSubscriptionLoading,
  } = useApiGetter(SubscriptionService, 'listSubscriptions', [], {
    suppressErrors: true,
    enable: can(Permission.READ_SUBSCRIPTIONS),
  });

  const isLoading = isCheckingStudentStatus || isSubscriptionLoading || isOrgStatsLoading;

  const hasAlreadyStudentPlan = useMemo(
    () =>
      !!subscriptions?.some(
        (sub) => sub.plan.family === PlanFamily.STUDENT && SUB_ACTIVE_STATUSES.includes(sub.status),
      ),
    [subscriptions],
  );

  useEffect(() => {
    const handleStudentPlanCheck = async () => {
      if (!code) {
        return;
      }

      const response = await confirmStudentPlan({ code });

      if (response.success) {
        await recordLegalAcceptance({
          document_type: DocumentType.MSA,
          source: LegalAcceptanceSource.STUDENT_VERIFICATION,
          context: 'accepted MSA when enrolling into student plan on /student-verification page',
        });
        mutate();
      } else {
        setVerificationFailed(true);
      }
    };

    handleStudentPlanCheck();
  }, [code]);

  const MessageDialog = useMemo((): ReactElement => {
    if (isLoading) {
      return <LoadingState />;
    }
    if (hasAlreadyStudentPlan) {
      return <EnrolledInProgram />;
    }
    if (!isSingleWorkspace) {
      return <ErrorMessage message="Student plan is only available for workspaces with one member." />;
    }
    if (verificationFailed) {
      return (
        <ErrorMessage message="Unfortunately, we are unable to verify your status as a participant in the GitHub Student Developer Pack. Visit the GitHub Student Pack Home for additional information, or create a free account." />
      );
    }
    return <EnrollInProgram />;
  }, [hasAlreadyStudentPlan, isSingleWorkspace, verificationFailed, isLoading]);

  return (
    <CustomerLayout title="Student">
      <CenterBox>
        <Box maxWidth={450}>
          <Typography variant="h3" component="h2" fontWeight={600} mb={2}>
            LocalStack for Students
          </Typography>
          <Typography fontSize={16} mb={3}>
            {STUDENT_PLAN_DESCRIPTION}
          </Typography>
          {MessageDialog}
          <StudentPlanLegalFineprint />
        </Box>
      </CenterBox>
    </CustomerLayout>
  );
};
